import type { ReactNode, CSSProperties } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useReducedMotion } from "framer-motion";

interface SortableExperienceShellProps {
  id:                  string;
  expIdx:              number;
  totalInDay:          number;
  hasPrevDay:          boolean;
  hasNextDay:          boolean;
  filtersActive:       boolean;
  editMode?:           boolean;
  insertionIndicator?: "above" | "below";
  onMoveUp:            () => void;
  onMoveDown:          () => void;
  onMoveToPrev:        () => void;
  onMoveToNext:        () => void;
  children:            ReactNode;
}

export function SortableExperienceShell({
  id, expIdx, totalInDay, hasPrevDay, hasNextDay, filtersActive, editMode = false,
  insertionIndicator,
  onMoveUp, onMoveDown, onMoveToPrev, onMoveToNext,
  children,
}: SortableExperienceShellProps) {
  const reduce = useReducedMotion() ?? false;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: filtersActive });

  const style: CSSProperties = {
    transform:  CSS.Transform.toString(transform),
    transition: reduce ? undefined : transition,
    opacity:    isDragging ? 0.35 : 1,
    position:   "relative",
    zIndex:     isDragging ? 1 : undefined,
  };

  const isFirstInDay = expIdx === 0;
  const isLastInDay  = expIdx === totalInDay - 1;

  // When in edit mode and filters are NOT active, the whole card row becomes the drag activator
  const fullCardDrag = editMode && !filtersActive;

  const indicator = (
    <div aria-hidden style={{ display: "flex", alignItems: "center", margin: "2px 8px" }}>
      <div style={{
        width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
        background: "oklch(0.38 0.10 262)",
        boxShadow: "0 0 0 2px oklch(0.38 0.10 262 / 0.20)",
      }} />
      <div style={{
        flex: 1, height: "2px",
        background: "linear-gradient(to right, oklch(0.38 0.10 262 / 0.80), oklch(0.38 0.10 262 / 0.15))",
        borderRadius: "1px",
      }} />
    </div>
  );

  // Props spread onto the outer flex wrapper only when fullCardDrag
  const outerDragProps = fullCardDrag
    ? { ref: setActivatorNodeRef, ...attributes, ...listeners }
    : {};

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={[
        "group/shell transition-colors duration-100",
        editMode && !filtersActive && !isDragging
          ? "hover:bg-[oklch(0.38_0.10_262_/_0.03)] rounded-lg"
          : "",
      ].join(" ")}
    >
      {insertionIndicator === "above" && indicator}
      <div
        {...outerDragProps}
        aria-label={fullCardDrag ? "Arrastrar actividad para reorganizar" : undefined}
        title={fullCardDrag ? "Arrastrar actividad para reorganizar" : undefined}
        className={[
          "flex items-stretch",
          fullCardDrag ? "cursor-grab active:cursor-grabbing" : "",
        ].join(" ")}
        style={fullCardDrag ? { touchAction: "none" } : undefined}
      >

        {/*
          Drag handle grip icon.
          fullCardDrag: purely visual (pointer-events-none, aria-hidden, no ref/listeners).
          Otherwise: the real activator with grab cursor.
        */}
        {fullCardDrag ? (
          <div
            aria-hidden
            tabIndex={-1}
            className="shrink-0 flex items-center justify-center select-none pointer-events-none min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:w-7"
            style={{ color: "oklch(0.38 0.10 262 / 0.65)" }}
          >
            <GripVertical size={18} />
          </div>
        ) : (
          <div
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            className={[
              "shrink-0 flex items-center justify-center select-none",
              "min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:w-7",
              filtersActive
                ? "opacity-20 cursor-not-allowed"
                : "cursor-grab active:cursor-grabbing sm:opacity-30 sm:group-hover/shell:opacity-100 sm:focus-visible:opacity-100 sm:transition-opacity touch-none",
            ].join(" ")}
            style={{
              color: "oklch(0.545 0.020 60 / 0.45)",
            }}
            aria-label="Arrastrar para reorganizar"
            title={filtersActive ? "Quita los filtros para reorganizar" : "Arrastrar para reorganizar"}
            tabIndex={filtersActive ? -1 : 0}
          >
            <GripVertical size={16} />
          </div>
        )}

        {/* Experience content */}
        <div className="flex-1 min-w-0">{children}</div>

        {/*
          Move controls:
          - Mobile: always visible, 2 buttons (▲ ▼) — moveUp/moveDown are cross-day aware at boundaries
          - Desktop: always visible (sm:opacity-100), 4 buttons
          Each touch target is ≥ 44×44px on mobile, 20×20 on desktop.
        */}
        <div className="shrink-0 flex items-center">
          {/* Mobile: ▲ ▼ only, large touch targets */}
          <div className="flex flex-col sm:hidden">
            <MoveBtn
              label="Subir"
              disabled={filtersActive || (isFirstInDay && !hasPrevDay)}
              onClick={onMoveUp}
              mobile
            >
              <ArrowUp size={14} />
            </MoveBtn>
            <MoveBtn
              label="Bajar"
              disabled={filtersActive || (isLastInDay && !hasNextDay)}
              onClick={onMoveDown}
              mobile
            >
              <ArrowDown size={14} />
            </MoveBtn>
          </div>

          {/* Desktop: all 4 buttons, vertical column, appear on hover */}
          <div className="hidden sm:flex flex-col items-center justify-center gap-px px-0.5 opacity-100 transition-opacity">
            <MoveBtn
              label="Subir"
              disabled={filtersActive || (isFirstInDay && !hasPrevDay)}
              onClick={onMoveUp}
            >
              <ArrowUp size={10} />
            </MoveBtn>
            <MoveBtn
              label="Al día anterior"
              disabled={filtersActive || !hasPrevDay}
              onClick={onMoveToPrev}
            >
              <ChevronLeft size={10} />
            </MoveBtn>
            <MoveBtn
              label="Al día siguiente"
              disabled={filtersActive || !hasNextDay}
              onClick={onMoveToNext}
            >
              <ChevronRight size={10} />
            </MoveBtn>
            <MoveBtn
              label="Bajar"
              disabled={filtersActive || (isLastInDay && !hasNextDay)}
              onClick={onMoveDown}
            >
              <ArrowDown size={10} />
            </MoveBtn>
          </div>
        </div>
      </div>
      {insertionIndicator === "below" && indicator}
    </div>
  );
}

function MoveBtn({
  label, disabled, onClick, children, mobile = false,
}: {
  label:    string;
  disabled: boolean;
  onClick:  () => void;
  children: ReactNode;
  mobile?:  boolean;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={[
        "flex items-center justify-center rounded transition-colors",
        mobile ? "w-11 h-11" : "w-5 h-5",
      ].join(" ")}
      style={{
        color:      disabled ? "oklch(0.545 0.020 60 / 0.18)" : "oklch(0.545 0.020 60 / 0.55)",
        background: "transparent",
        cursor:     disabled ? "default" : "pointer",
      }}
    >
      {children}
    </button>
  );
}
